import React from 'react';
import { Card, Tag, Space } from 'antd';
import { EyeOutlined, LikeOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import './index.css';

// 在 PostCard/index.jsx 的 import 语句后添加这个增强的调色板
const gradientPalettes = {
  // 1. 柔和系列 (适合生活、随笔类文章)
  soft: [
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', // 蓝粉渐变
    'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)', // 黄绿渐变
    'linear-gradient(135deg, #fddb92 0%, #d1fdff 100%)', // 橙蓝渐变
    'linear-gradient(135deg, #a3bded 0%, #6991c7 100%)', // 深浅蓝渐变
    'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)', // 粉紫渐变
  ],
  
  // 2. 活力系列 (适合旅行、户外类文章)
  vibrant: [
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', // 红粉渐变
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', // 紫粉渐变
    'linear-gradient(135deg, #fad0c4 0%, #ff9a9e 100%)', // 橙红渐变
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', // 橙黄渐变
    'linear-gradient(135deg, #ff8177 0%, #ff867a 0%, #ff8c7f 21%, #f99185 52%, #cf556c 78%, #b12a5b 100%)', // 多彩渐变
  ],
  
  // 3. 深沉系列 (适合电影、思考类文章)
  deep: [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // 深紫蓝渐变
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', // 青紫渐变
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // 蓝青渐变
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // 青绿渐变
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // 粉黄渐变
  ],
  
  // 4. 自然系列 (适合植物、美食类文章)
  nature: [
    'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)', // 天空渐变
    'linear-gradient(135deg, #b3ffab 0%, #12fff7 100%)', // 草地渐变
    'linear-gradient(135deg, #f6d365 0%, #fda085 100%)', // 日落渐变
    'linear-gradient(135deg, #a6c0fe 0%, #f68084 100%)', // 花丛渐变
    'linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)', // 海洋渐变
  ],
  
  // 5. 梦幻系列 (适合艺术、创意类文章)
  dreamy: [
    'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)', // 粉金渐变
    'linear-gradient(135deg, #cd9cf2 0%, #f6f3ff 100%)', // 紫白渐变
    'linear-gradient(135deg, #a8c0ff 0%, #3f2b96 100%)', // 星空渐变
    'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)', // 柔和红橙渐变
    'radial-gradient(circle at 10% 20%, rgb(253, 239, 132) 0%, rgb(247, 198, 169) 54.2%, rgb(21, 186, 196) 100.3%)', // 径向渐变
  ]
};

// 根据文章标签智能选择调色板
const getPaletteByTags = (tags) => {
  if (!tags || tags.length === 0) return 'soft';
  
  const tagStr = tags.join(' ').toLowerCase();
  
  if (tagStr.includes('旅行') || tagStr.includes('户外') || tagStr.includes('冒险')) {
    return 'vibrant';
  } else if (tagStr.includes('电影') || tagStr.includes('思考') || tagStr.includes('哲学')) {
    return 'deep';
  } else if (tagStr.includes('美食') || tagStr.includes('植物') || tagStr.includes('自然')) {
    return 'nature';
  } else if (tagStr.includes('艺术') || tagStr.includes('创意') || tagStr.includes('设计')) {
    return 'dreamy';
  } else {
    return 'soft';
  }
};

// 生成唯一的渐变背景
const generateGradient = (title, tags) => {
  const paletteType = getPaletteByTags(tags);
  const palette = gradientPalettes[paletteType];
  
  // 使用标题长度和字符决定具体颜色
  const titleHash = Array.from(title).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const gradientIndex = titleHash % palette.length;
  
  return palette[gradientIndex];
};

// 生成辅助色（用于文字背景等）
const generateAccentColor = (title) => {
  const colors = [
    'rgba(255, 255, 255, 0.25)',
    'rgba(255, 255, 255, 0.3)',
    'rgba(255, 255, 255, 0.2)',
    'rgba(255, 255, 255, 0.15)',
    'rgba(0, 0, 0, 0.1)',
    'rgba(0, 0, 0, 0.15)'
  ];
  
  const index = title.length % colors.length;
  return colors[index];
};



const { Meta } = Card;

const PostCard = ({ post }) => {
  return (
    <Card
      className="post-card"
      hoverable
      // 在 PostCard/index.jsx 中替换 cover 部分
// 在 PostCard 组件中修改 cover 部分
cover={
  <div className="post-cover">
    {post.image ? (
      <img 
        src={post.image} 
        alt={post.title}
        className="post-cover-image"
      />
    ) : (
      <div 
        className="dynamic-cover"
        style={{
          background: generateGradient(post.title, post.tags)
        }}
      >
        <div className="cover-content">
          <div className="title-container">
            <span className="cover-main-title">
              {post.title.substring(0, 6)}
            </span>
            {post.title.length > 6 && (
              <span className="cover-sub-title">
                {post.title.substring(6, 12)}
              </span>
            )}
          </div>
          
          {post.tags.length > 0 && (
            <div className="tags-container">
              {post.tags.slice(0, 2).map((tag, index) => (
                <span 
                  key={tag}
                  className="cover-tag"
                  style={{
                    background: generateAccentColor(tag + index),
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  {tag}
                </span>
              ))}
              {post.tags.length > 2 && (
                <span className="cover-tag-more">
                  +{post.tags.length - 2}
                </span>
              )}
            </div>
          )}
          
          {/* 添加装饰性元素 */}
          <div className="cover-decoration">
            <div className="decoration-circle" style={{top: '10%', left: '15%'}}></div>
            <div className="decoration-circle" style={{bottom: '20%', right: '10%'}}></div>
          </div>
        </div>
      </div>
    )}
  </div>
}
      actions={[
        <Space key="views">
          <EyeOutlined />
          <span>{post.views}</span>
        </Space>,
        <Space key="likes">
          <LikeOutlined />
          <span>{post.likes}</span>
        </Space>
      ]}
    >
      <Meta
        title={
          <Link to={`/post/${post.id}`} className="post-title">
            {post.title}
          </Link>
        }
        description={
          <>
            <p className="post-summary">{post.summary}</p>
            <div className="post-meta">
              <Space>
                <UserOutlined />
                <span>{post.author}</span>
                <CalendarOutlined />
                <span>{post.date}</span>
              </Space>
            </div>
            <div className="post-tags">
              {post.tags.map(tag => (
                <Tag key={tag} color="blue">{tag}</Tag>
              ))}
            </div>
          </>
        }
      />
    </Card>
  );
};

export default PostCard;